<template>
    <div class="file-upload-form">
        <div
            class="dropzone-area mb-3 d-flex flex-column align-items-center justify-content-center text-center p-4 border rounded position-relative"
            :class="{ 'is-dragging': isDragging, 'has-files': localFiles.length > 0 }"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
            @click="$refs.fileInput.click()"
        >
            <input ref="fileInput" type="file" multiple class="d-none" accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx" @change="handleFileSelect" />

            <feather-icon icon="UploadCloudIcon" size="48" class="text-primary mb-2" />
            <h5 class="mb-50 text-dark">Dosyaları buraya sürükleyin veya seçin</h5>
            <p class="text-muted small mb-0">Maksimum dosya boyutu: 25MB</p>
        </div>

        <div v-if="localFiles.length > 0" class="selected-files mb-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="mb-0 text-primary">
                    <feather-icon icon="CheckCircleIcon" size="16" class="mr-50" />
                    Seçilen Dosyalar ({{ localFiles.length }})
                </h6>
                <b-button variant="flat-danger" size="sm" class="py-0" @click="clearFiles"> Temizle </b-button>
            </div>

            <div class="d-flex flex-wrap gap-2">
                <div v-for="(file, index) in localFiles" :key="index" class="file-preview-item border rounded position-relative theme-item" v-b-tooltip.hover :title="file.name">
                    <img v-if="isImage(file)" :src="file.preview" class="img-fluid rounded" />
                    <div v-else class="d-flex align-items-center justify-content-center h-100 bg-light rounded">
                        <feather-icon icon="FileIcon" size="24" class="text-muted" />
                    </div>

                    <div class="remove-btn cursor-pointer bg-danger text-white rounded-circle d-flex align-items-center justify-content-center" @click.stop="removeFile(index)">
                        <feather-icon icon="XIcon" size="12" />
                    </div>
                </div>
            </div>
        </div>

        <hr />

        <div class="metadata-form">
            <h6 class="mb-2 d-flex align-items-center">
                <feather-icon icon="SettingsIcon" size="16" class="mr-50" />
                Dosya Bilgileri (Toplu Uygulanır)
            </h6>

            <b-row>
                <b-col md="6">
                    <b-form-group label="Dosya Anahtarı" label-for="fileKey">
                        <b-form-input id="fileKey" v-model="localValue.fileKey" placeholder="slider-image-1" size="sm" />
                    </b-form-group>
                </b-col>
                <b-col md="6">
                    <b-form-group label="Tag'ler" label-for="tags">
                        <b-form-input id="tags" v-model="localValue.tags" placeholder="galeri,2024,yeni" size="sm" />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="6">
                    <b-form-group label="Başlık" label-for="title">
                        <b-form-input id="title" v-model="localValue.title" placeholder="Dosya başlığı" size="sm" />
                    </b-form-group>
                </b-col>
                <b-col md="6">
                    <b-form-group label="Alt Text (SEO)" label-for="alt">
                        <b-form-input id="alt" v-model="localValue.alt" placeholder="Alternatif metin" size="sm" />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="12">
                    <b-form-group label="Açıklama" label-for="description">
                        <b-form-textarea id="description" v-model="localValue.description" rows="2" placeholder="Dosya açıklaması" size="sm" />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="4">
                    <b-form-group label="Sıra" label-for="sortOrder">
                        <b-form-input id="sortOrder" v-model.number="localValue.sortOrder" type="number" size="sm" />
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group label="Dil" label-for="languageCode">
                        <v-select-list id="languageCode" v-model="localValue.languageCode" :options="languages" label="LanguageName" :reduce="(l) => l.LanguageCode" placeholder="Seçiniz" />
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group label="Durum" label-for="isActive">
                        <b-form-checkbox id="isActive" v-model="localValue.isActive" switch>
                            {{ localValue.isActive ? "Aktif" : "Pasif" }}
                        </b-form-checkbox>
                    </b-form-group>
                </b-col>
            </b-row>
        </div>
    </div>
</template>

<script>
import { BRow, BCol, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, BButton, VBTooltip } from "bootstrap-vue";
import VSelectList from "@/components/VSelectList.vue";

export default {
    name: "FileUploadForm",
    components: {
        BRow,
        BCol,
        BFormGroup,
        BFormInput,
        BFormTextarea,
        BFormCheckbox,
        BButton,
        VSelectList,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        value: { type: Object, required: true },
        errors: { type: Object, default: () => ({}) },
        languages: { type: Array, default: () => [] },
    },
    data() {
        return {
            isDragging: false,
            localFiles: [],
        };
    },
    computed: {
        localValue: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit("input", val);
            },
        },
    },
    methods: {
        handleDrop(e) {
            this.isDragging = false;
            const files = e.dataTransfer.files;
            this.processFiles(files);
        },
        handleFileSelect(e) {
            const files = e.target.files;
            this.processFiles(files);
        },
        processFiles(files) {
            if (!files.length) return;

            const newFiles = Array.from(files).map((file) => {
                file.preview = URL.createObjectURL(file);
                return file;
            });

            this.localFiles = [...this.localFiles, ...newFiles];
            this.$emit("files-selected", this.localFiles);
        },
        removeFile(index) {
            this.localFiles.splice(index, 1);
            this.$emit("files-selected", this.localFiles);
        },
        clearFiles() {
            this.localFiles = [];
            this.$emit("files-selected", this.localFiles);
        },
        isImage(file) {
            return file.type.startsWith("image/");
        },
    },
    beforeDestroy() {
        this.localFiles.forEach((file) => {
            if (file.preview) URL.revokeObjectURL(file.preview);
        });
    },
};
</script>

<style scoped>
.dropzone-area {
    border: 2px dashed #d8d6de;
    background-color: #f8f9fa;
    transition: all 0.3s ease;
    cursor: pointer;
}
.dropzone-area:hover,
.dropzone-area.is-dragging {
    border-color: #7367f0;
    background-color: rgba(115, 103, 240, 0.05);
}
.dropzone-area.has-files {
    border-color: #28c76f;
}

.file-preview-item {
    width: 80px;
    height: 80px;
    overflow: hidden;
    border: 1px solid #ebe9f1;
}
.file-preview-item img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
.remove-btn {
    position: absolute;
    top: -5px;
    right: -5px;
    width: 18px;
    height: 18px;
    z-index: 10;
    border: 1px solid white;
}

.dark-layout .dropzone-area {
    background-color: #161d31;
    border-color: #3b4253;
}
.dark-layout .dropzone-area .text-dark {
    color: #d0d2d6 !important;
}
.dark-layout .file-preview-item {
    border-color: #3b4253;
}
.dark-layout .bg-light {
    background-color: #283046 !important;
}
</style>
